import React, { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, Stack, Typography } from '@mui/material';
import { ROPatientCarePlanRoute } from '../Interface';
import { CAREPLAN_PAGES } from '../Constants';
import { CareplanBanners } from '../Banner/Banner';
import { GET_CAREPLAN } from '../Queries';
import ROPatientCarePlanPageFooter from '../Footer';
import { ROToggleButtons, ROTextField } from 'shared-components/components/FormFields';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';
import CareplanContainer from '../Container';
import { LoadingSpinner } from 'shared-components/components';
import { BOOLEAN_UNKNOWN_OPTIONS } from '../TreatmentInfoPage/Interface';
import { useFormikContext, FormikProps } from 'formik';
import ROPatientCarePathSidePanel from '../SidePanel/SidePanel';

const GET_MRL_SAFETY_SCREENING = gql`
  query mrlSafetyScreening($careplanId: ID!) {
    mrlSafetyScreening(careplanId: $careplanId) {
      id
      neurostimulator
      pacemakerInSitu
      aneurysmClips
      metalHips
      heartSurgery
      otherImplants
    }
  }
`;

const UPSERT_MRL_SAFETY_SCREENING = gql`
  mutation upsertMrlSafetyScreening($careplanId: ID!, $input: MRLSafetyScreeningInput!) {
    upsertMrlSafetyScreening(careplanId: $careplanId, input: $input) {
      mrlSafetyScreening {
        id
        neurostimulator
        pacemakerInSitu
        aneurysmClips
        metalHips
        heartSurgery
        otherImplants
      }
    }
  }
`;

const MRLSafetyScreeningPage = (): JSX.Element => {
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const history = useHistory();
  const { id: patientId, careplanId } = match.params;
  const tenantAppConfig = useTenantAppConfig();

  const {
    data: mrlQuery,
    loading,
    refetch,
  } = useQuery(GET_MRL_SAFETY_SCREENING, {
    variables: { careplanId },
    skip: !careplanId || careplanId === 'create',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const s = data?.mrlSafetyScreening;
      if (!s) return;
      setFieldValue('mrlSafetyScreening', {
        neurostimulator: s.neurostimulator || '',
        pacemakerInSitu: s.pacemakerInSitu || '',
        aneurysmClips: s.aneurysmClips || '',
        metalHips: s.metalHips || '',
        heartSurgery: s.heartSurgery || '',
        otherImplants: s.otherImplants || '',
      });
    },
  });

  const { data: careplanStatusData } = useQuery(GET_CAREPLAN, {
    fetchPolicy: 'network-only',
    variables: { id: careplanId },
  });

  const [upsert] = useMutation(UPSERT_MRL_SAFETY_SCREENING, {
    awaitRefetchQueries: true,
    refetchQueries: [
      { query: GET_MRL_SAFETY_SCREENING, variables: { careplanId } },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
  });
  const { values, setFieldValue }: FormikProps<any> = useFormikContext();
  const screening = (values as any)?.mrlSafetyScreening || {};
  const [pressedContinue, setPressedContinue] = React.useState<boolean>(false);

  const updateHandler = (changedValues: { [key: string]: string | null }): void => {
    Object.keys(changedValues).forEach((key) => setFieldValue(`mrlSafetyScreening.${key}`, changedValues[key]));
    // send all the formik values to the backend to keep the state in sync
    const newValues = { ...values.mrlSafetyScreening, ...changedValues };
    upsert({
      variables: {
        careplanId,
        input: newValues,
      },
    });
  };

  const isFieldValid = (field: string | null | undefined): boolean => {
    if (!pressedContinue) return true;
    return Boolean(field && `${field}`.length > 0);
  };

  const areRequiredFieldsValid = (): boolean => {
    return [
      screening.neurostimulator,
      screening.pacemakerInSitu,
      screening.aneurysmClips,
      screening.metalHips,
      screening.heartSurgery,
    ].every((v: any) => Boolean(v && `${v}`.length > 0));
  };

  if (loading) return <LoadingSpinner loadingText={'Loading MRL Safety Screening'} subtitle={''} />;

  return (
    <Stack direction="row" sx={{ flex: 1, overflowY: 'auto' }}>
      <Stack sx={{ flexGrow: 1 }}>
        <CareplanBanners
          data={careplanStatusData}
          refetchQueriesList={[{ query: GET_CAREPLAN, variables: { id: careplanId } }]}
          forceUpdate={() => {
            refetch();
          }}
        />
        <CareplanContainer>
          <Stack spacing={2} flex={1}>
            <Typography variant="h5" data-testid="page-header">
              MRL Safety Screening
            </Typography>
            <Box component="form" id="mrlSafetyScreeningForm" noValidate>
              <ROToggleButtons
                id="neurostimulator"
                fieldlabel="Neurostimulator"
                options={BOOLEAN_UNKNOWN_OPTIONS}
                value={screening.neurostimulator || ''}
                handleChange={(value: string): void => {
                  updateHandler({ neurostimulator: value });
                }}
                required
                error={!isFieldValid(screening.neurostimulator)}
              />
              <ROToggleButtons
                id="pacemakerInSitu"
                fieldlabel="Pacemaker in situ"
                options={BOOLEAN_UNKNOWN_OPTIONS}
                value={screening.pacemakerInSitu || ''}
                handleChange={(value: string): void => {
                  updateHandler({ pacemakerInSitu: value });
                }}
                required
                error={!isFieldValid(screening.pacemakerInSitu)}
              />
              <ROToggleButtons
                id="aneurysmClips"
                fieldlabel="Aneurysm clips"
                options={BOOLEAN_UNKNOWN_OPTIONS}
                value={screening.aneurysmClips || ''}
                handleChange={(value: string): void => {
                  updateHandler({ aneurysmClips: value });
                }}
                required
                error={!isFieldValid(screening.aneurysmClips)}
              />
              <ROToggleButtons
                id="metalHips"
                fieldlabel="Metal Hips"
                options={BOOLEAN_UNKNOWN_OPTIONS}
                value={screening.metalHips || ''}
                handleChange={(value: string): void => {
                  updateHandler({ metalHips: value });
                }}
                required
                error={!isFieldValid(screening.metalHips)}
              />
              <ROToggleButtons
                id="heartSurgery"
                fieldlabel="Heart Surgery"
                options={BOOLEAN_UNKNOWN_OPTIONS}
                value={screening.heartSurgery || ''}
                handleChange={(value: string): void => {
                  updateHandler({ heartSurgery: value });
                }}
                required
                error={!isFieldValid(screening.heartSurgery)}
              />
              <ROTextField
                id="otherImplants"
                fieldlabel="Other (implants, wires, etc)"
                value={screening.otherImplants || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  setFieldValue('mrlSafetyScreening.otherImplants', event.target.value);
                }}
                onBlur={(): void => {
                  updateHandler({
                    otherImplants: (screening.otherImplants || '').trim() === '' ? null : screening.otherImplants,
                  });
                }}
                multiline
                maxRows={4}
              />
            </Box>
          </Stack>
        </CareplanContainer>
        <ROPatientCarePlanPageFooter
          resetHidden={true}
          onBack={() =>
            history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${CAREPLAN_PAGES.VOLUMING}/last`)
          }
          onNext={async () => {
            setPressedContinue(true);
            if (!areRequiredFieldsValid()) return;
            const nextPage = tenantAppConfig.SpecialPhysicsConsultVisible
              ? CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT
              : CAREPLAN_PAGES.SUBMISSION;
            history.push(`/radiation/patient/${patientId}/careplan/${careplanId}/${nextPage}`);
          }}
        />
      </Stack>
      <ROPatientCarePathSidePanel />
    </Stack>
  );
};

export default MRLSafetyScreeningPage;
